/**
 * Shared light/dark theme-class application, used by both the app's initial
 * boot (src/index.jsx, before React renders) and the account-menu Sun/Moon
 * toggle (UserAvatar's dropdown) — one place that knows how a saved `theme`
 * value maps onto <html>/<body> classes, instead of two copies drifting
 * apart.
 *
 * `theme` supports the full named-theme surface (dark, light, noctis,
 * catppuccin-mocha, high-contrast, ...) — this module doesn't limit that.
 * The account-menu toggle built on top of it is deliberately simpler: a
 * plain two-state Sun/Moon control, matching the Mia Platform Console
 * account menu it's modeled on, not the full multi-theme picker.
 */

const ALL_THEME_CLASSES = [
  'dark-mode',
  'theme-noctis',
  'theme-github',
  'theme-synthwave',
  'theme-hackerx-green',
  'theme-hackerx-ocean',
  'theme-monokai',
  'theme-catppuccin-latte',
  'theme-catppuccin-frappe',
  'theme-catppuccin-macchiato',
  'theme-catppuccin-mocha',
  'theme-high-contrast',
  'theme-light-high-contrast',
];

const LIGHT_FAMILY_THEMES = ['light', 'monokai', 'catppuccin-latte', 'light-high-contrast'];

/** Applies `theme`'s classes to <html>/<body>. Mirrors index.jsx's original inline logic exactly. */
export function applyThemeClasses(theme) {
  const root = document.documentElement;
  const body = document.body;

  root.classList.remove(...ALL_THEME_CLASSES);
  body.classList.remove(...ALL_THEME_CLASSES);

  if (LIGHT_FAMILY_THEMES.includes(theme)) {
    if (theme === 'light-high-contrast') {
      root.classList.add('theme-light-high-contrast');
      body.classList.add('theme-light-high-contrast');
    } else if (theme !== 'light') {
      root.classList.add(`theme-${theme}`);
      body.classList.add(`theme-${theme}`);
    }
    // plain 'light' adds no class — it's the unstyled default.
  } else if (theme === 'dark') {
    root.classList.add('dark-mode');
    body.classList.add('dark-mode');
  } else if (theme === 'high-contrast') {
    root.classList.add('dark-mode', 'theme-high-contrast');
    body.classList.add('dark-mode', 'theme-high-contrast');
  } else {
    root.classList.add('dark-mode', `theme-${theme}`);
    body.classList.add('dark-mode', `theme-${theme}`);
  }
}

/**
 * Simple light/dark reading for the two-state account-menu toggle: any
 * light-family named theme reads as 'light', everything else as 'dark'.
 */
export function getThemeMode() {
  const saved = localStorage.getItem('theme') || 'light';
  return LIGHT_FAMILY_THEMES.includes(saved) ? 'light' : 'dark';
}

/**
 * Sets the plain 'light' or 'dark' base theme and applies it immediately.
 * Switching mode here intentionally collapses a previously-picked named
 * accent theme (e.g. 'catppuccin-mocha') down to the plain base — this is
 * the simple two-state toggle, not the full multi-theme picker.
 */
export function setThemeMode(mode) {
  localStorage.setItem('theme', mode);
  applyThemeClasses(mode);
}
